import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import {
    buildOnboardingActivationPath,
    onboardingFinalizationEndpointCandidates,
    onboardingVerificationEndpointCandidates,
    OnboardingSubscriptionRequiredError,
    parseOnboardingFinalizationError,
    parseOnboardingIntentFinalization,
    parseOnboardingProjectIntent,
    parseOnboardingProjectPreview,
    parseOnboardingVerificationAccepted,
    type OnboardingIntentFinalization,
    type OnboardingJourneySource,
    type OnboardingProjectIntent,
    type OnboardingProjectPreview,
    type OnboardingVerificationAccepted,
} from './onboardingProjectIntentContract';
import { ensureDiscoverySession, supabase, toError } from './supabase';
import {
    buildOnboardingIntentRpcRequest,
    hasExpectedOnboardingSeedBinding,
    resolveStoredOnboardingIntentSeed,
} from './onboardingProjectIntentRequest';

const STORAGE_PREFIX = 'pz_onboarding_project_intent_v1:';

export interface StoredOnboardingProjectIntent {
    intent: OnboardingProjectIntent;
    createClientRequestId: string;
    directFinalizeClientRequestId: string;
    finalizationRequestId: string | null;
    journeySource: OnboardingJourneySource;
    chatSessionId: string | null;
    generatedProjectName: string;
    seedPaperId: string | null;
    verificationGeneration: string | null;
}

interface CreateOrResumeIntentInput {
    projectId: string;
    clientRequestId: string;
    journeySource: OnboardingJourneySource;
    chatSessionId?: string | null;
    generatedProjectName: string;
    seedPaperId?: string | null;
}

interface RequestVerificationInput {
    intent: OnboardingProjectIntent;
    email: string;
    verificationGeneration: string;
    clientRequestId: string;
}

function storageKey(projectId: string): string {
    return `${STORAGE_PREFIX}${projectId}`;
}

export function createOnboardingRequestId(): string {
    const randomUUID = globalThis.crypto?.randomUUID;
    if (typeof randomUUID === 'function') {
        return randomUUID.call(globalThis.crypto);
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const random = Math.floor(Math.random() * 16);
        const value = character === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

function unwrapRpcResponse(value: unknown): unknown {
    if (Array.isArray(value) && value.length === 1) {
        return value[0];
    }
    return value;
}

function isStoredIntent(value: unknown): value is StoredOnboardingProjectIntent {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as Partial<StoredOnboardingProjectIntent>;
    return (
        typeof candidate.createClientRequestId === 'string'
        && typeof candidate.directFinalizeClientRequestId === 'string'
        && (
            candidate.finalizationRequestId === null
            || typeof candidate.finalizationRequestId === 'string'
        )
        && (
            candidate.journeySource === 'chat'
            || candidate.journeySource === 'forward'
            || candidate.journeySource === 'direct'
        )
        && (candidate.chatSessionId === null || typeof candidate.chatSessionId === 'string')
        && typeof candidate.generatedProjectName === 'string'
        && (
            candidate.seedPaperId === undefined
            || candidate.seedPaperId === null
            || typeof candidate.seedPaperId === 'string'
        )
        && (
            candidate.verificationGeneration === null
            || typeof candidate.verificationGeneration === 'string'
        )
    );
}

export async function saveOnboardingProjectIntent(
    stored: StoredOnboardingProjectIntent
): Promise<void> {
    await AsyncStorage.setItem(storageKey(stored.intent.projectId), JSON.stringify(stored));
}

export async function loadOnboardingProjectIntent(
    projectId: string
): Promise<StoredOnboardingProjectIntent | null> {
    try {
        const raw = await AsyncStorage.getItem(storageKey(projectId));
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!isStoredIntent(parsed)) {
            await AsyncStorage.removeItem(storageKey(projectId));
            return null;
        }
        const intent = parseOnboardingProjectIntent({
            intent_id: (parsed as any).intent?.intentId,
            project_id: (parsed as any).intent?.projectId,
            activation_generation: (parsed as any).intent?.activationGeneration,
            status: (parsed as any).intent?.status,
            expires_at: (parsed as any).intent?.expiresAt,
            verification_required: (parsed as any).intent?.verificationRequired,
        });
        if (intent.projectId !== projectId) {
            await AsyncStorage.removeItem(storageKey(projectId));
            return null;
        }
        return {
            ...parsed,
            intent,
            seedPaperId: typeof (parsed as any).seedPaperId === 'string'
                ? (parsed as any).seedPaperId
                : null,
        } as StoredOnboardingProjectIntent;
    } catch {
        await AsyncStorage.removeItem(storageKey(projectId));
        return null;
    }
}

export async function clearOnboardingProjectIntent(projectId: string): Promise<void> {
    await AsyncStorage.removeItem(storageKey(projectId));
}

export async function createOrResumeOnboardingProjectIntent({
    projectId,
    clientRequestId,
    journeySource,
    chatSessionId = null,
    generatedProjectName,
    seedPaperId = null,
}: CreateOrResumeIntentInput): Promise<StoredOnboardingProjectIntent> {
    await ensureDiscoverySession();
    const request = buildOnboardingIntentRpcRequest({
        projectId,
        clientRequestId,
        journeySource,
        chatSessionId,
        generatedProjectName,
        seedPaperId,
    });
    const { data, error } = await supabase.rpc(
        request.functionName as any,
        request.args as any
    );
    if (error) {
        throw toError(error);
    }
    const response = unwrapRpcResponse(data);
    const intent = parseOnboardingProjectIntent(response);
    if (intent.projectId !== projectId) {
        throw new Error('Onboarding intent response project binding mismatch.');
    }
    if (!hasExpectedOnboardingSeedBinding(response, seedPaperId)) {
        throw new Error('Onboarding intent response paper-seed binding mismatch.');
    }
    const stored: StoredOnboardingProjectIntent = {
        intent,
        createClientRequestId: clientRequestId,
        directFinalizeClientRequestId: createOnboardingRequestId(),
        finalizationRequestId: null,
        journeySource,
        chatSessionId,
        generatedProjectName,
        seedPaperId,
        verificationGeneration: null,
    };
    await saveOnboardingProjectIntent(stored);
    return stored;
}

export async function loadOrResumeOnboardingProjectIntent({
    projectId,
    journeySource,
    chatSessionId = null,
    generatedProjectName,
    seedPaperId = null,
}: Omit<CreateOrResumeIntentInput, 'clientRequestId'>): Promise<StoredOnboardingProjectIntent> {
    const stored = await loadOnboardingProjectIntent(projectId);
    if (stored && Date.parse(stored.intent.expiresAt) > Date.now()) {
        const seedDecision = resolveStoredOnboardingIntentSeed({
            storedCreateClientRequestId: stored.createClientRequestId,
            storedSeedPaperId: stored.seedPaperId,
            requestedSeedPaperId: seedPaperId,
        });
        if (seedDecision.kind === 'reuse') {
            return stored;
        }

        return createOrResumeOnboardingProjectIntent({
            projectId,
            clientRequestId: seedDecision.clientRequestId,
            journeySource: stored.journeySource,
            chatSessionId: stored.chatSessionId,
            generatedProjectName: stored.generatedProjectName,
            seedPaperId: seedDecision.seedPaperId,
        });
    }
    return createOrResumeOnboardingProjectIntent({
        projectId,
        clientRequestId: createOnboardingRequestId(),
        journeySource,
        chatSessionId,
        generatedProjectName,
        seedPaperId,
    });
}

export async function getOnboardingProjectPreview(
    intentId: string
): Promise<OnboardingProjectPreview> {
    const { data, error } = await supabase.rpc(
        'get_onboarding_project_preview' as any,
        { p_intent_id: intentId } as any
    );
    if (error) {
        throw toError(error);
    }
    return parseOnboardingProjectPreview(unwrapRpcResponse(data));
}

function currentWebHostname(): string | undefined {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return undefined;
    }
    return window.location.hostname;
}

export async function requestOnboardingProjectVerification({
    intent,
    email,
    verificationGeneration,
    clientRequestId,
}: RequestVerificationInput): Promise<OnboardingVerificationAccepted> {
    const { session } = await ensureDiscoverySession();
    if (!session?.access_token) {
        throw new Error('Your session could not be restored. Please reload and try again.');
    }

    let lastError: Error | null = null;
    const candidates = onboardingVerificationEndpointCandidates({
        isWeb: Platform.OS === 'web',
        hostname: currentWebHostname(),
        intentId: intent.intentId,
    });
    for (const endpoint of candidates) {
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    verification_generation: verificationGeneration,
                    client_request_id: clientRequestId,
                }),
                cache: 'no-store',
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
            });
            if (response.status !== 202) {
                throw new Error(
                    response.status === 429
                        ? 'Please wait before requesting another code.'
                        : 'We could not confirm the code request. Check your inbox before trying again.'
                );
            }
            const accepted = parseOnboardingVerificationAccepted(
                await response.json(),
                intent.intentId,
                verificationGeneration
            );
            const stored = await loadOnboardingProjectIntent(intent.projectId);
            if (stored && stored.intent.intentId === intent.intentId) {
                await saveOnboardingProjectIntent({
                    ...stored,
                    intent: {
                        ...stored.intent,
                        status: 'verification_requested',
                        expiresAt: accepted.expiresAt,
                    },
                    verificationGeneration,
                    finalizationRequestId: accepted.finalizationRequestId,
                });
            }
            return accepted;
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error));
        }
    }
    throw lastError || new Error(
        'We could not confirm the code request. Check your inbox before trying again.'
    );
}

export async function finalizeOnboardingProjectIntent(
    intent: Pick<OnboardingProjectIntent, 'intentId' | 'projectId' | 'activationGeneration'>,
    finalizationRequestId: string
): Promise<OnboardingIntentFinalization> {
    if (!finalizationRequestId) {
        throw new Error('Project confirmation could not be restored. Reload and try again.');
    }
    const { session } = await ensureDiscoverySession();
    if (!session?.access_token) {
        throw new Error('Your session could not be restored. Please reload and try again.');
    }
    let lastError: Error | null = null;
    const candidates = onboardingFinalizationEndpointCandidates({
        isWeb: Platform.OS === 'web',
        hostname: currentWebHostname(),
        intentId: intent.intentId,
    });
    for (const endpoint of candidates) {
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    activation_generation: intent.activationGeneration,
                    finalization_request_id: finalizationRequestId,
                }),
                cache: 'no-store',
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
            });
            if (response.status !== 200) {
                throw parseOnboardingFinalizationError(
                    response.status,
                    response.status === 403 ? await response.json().catch(() => null) : null
                );
            }
            const result = parseOnboardingIntentFinalization(
                await response.json(),
                intent
            );
            await clearOnboardingProjectIntent(intent.projectId);
            return result;
        } catch (error) {
            if (error instanceof OnboardingSubscriptionRequiredError) throw error;
            lastError = error instanceof Error ? error : new Error(String(error));
        }
    }
    throw lastError || new Error('We could not open your project. Please try again.');
}

export function onboardingActivationDestination(projectId: string): string {
    return buildOnboardingActivationPath(projectId);
}
